import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../../theme/useTheme';
import Button from '../Button';
import { FormStatus } from '../FormStatus';
import Text from '../Text';
import { ClientLogo } from './ClientLogo';
import { DotsConnector } from './DotsConnector';
import { TTLogo } from './Logos';
import { PermissionsList } from './PermissionsList';
import { useMcpClient } from './useMcpClient';
import { useMcpConsent } from './useMcpConsent';

export default function McpAuthorize() {
  useTheme();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const consentParams = {
    clientId: params.get('client_id') ?? '',
    redirectUri: params.get('redirect_uri') ?? '',
    state: params.get('state') ?? '',
    codeChallenge: params.get('code_challenge') ?? '',
    codeChallengeMethod: params.get('code_challenge_method') ?? '',
    scope: params.get('scope') ?? '',
  };
  const { client, loading: clientLoading, error: clientError } = useMcpClient(consentParams);
  const { loading, error, handleApprove, handleDeny } = useMcpConsent(consentParams);
  const clientName = client?.client_name || 'Your MCP client';

  return (
    <div className="flex flex-col items-center justify-start pt-20 px-4">
      <div className="flex flex-col gap-10 items-center w-full max-w-[405px]">
        <div className="flex gap-5 items-center">
          <div className="size-[70px] rounded-xl bg-light-selected-faint dark:bg-dark-hover-faint border border-control flex items-center justify-center">
            <TTLogo />
          </div>
          <DotsConnector />
          <div className="size-[70px] rounded-xl bg-light-selected-faint dark:bg-dark-hover-faint border border-control flex items-center justify-center">
            <ClientLogo clientName={clientName} logoUri={client?.logo_uri} />
          </div>
        </div>

        <div className="flex flex-col gap-[10px] text-center w-full">
          <Text variant="base" as="p" className="font-semibold text-[22px]">
            {clientLoading
              ? 'Loading client details...'
              : client
                ? `Connect ${clientName} to TokenTax`
                : 'Unable to authorize this connection'}
          </Text>
          {client && (
            <Text variant="muted" as="p">
              {clientName} requests read and write access
            </Text>
          )}
        </div>

        {client && <PermissionsList permissions={client.permissions} />}

        {(clientError || error) && (
          <FormStatus type="error" className="w-full">
            {clientError || error}
          </FormStatus>
        )}

        <div className="flex flex-col gap-[10px] items-center w-full">
          {client && (
            <div className="text-center w-full">
              {!client.is_local && (
                <Text variant="base" as="p">
                  Connects to: <bdi className="break-all">{client.redirect_hostname}</bdi>
                </Text>
              )}
              {client.is_local && (
                <Text variant="muted" as="p">
                  Continue only if you started this from an app on this device.
                </Text>
              )}
            </div>
          )}
          <Button
            fullWidth
            className="rounded-[10px]"
            onClick={handleApprove}
            loading={loading}
            disabled={loading || clientLoading || !client}
          >
            Authorize
          </Button>
          <Button
            fullWidth
            variant="secondary"
            className="rounded-[10px]"
            onClick={handleDeny}
            disabled={loading || clientLoading || !client}
          >
            Deny
          </Button>
          <Text variant="muted" as="p">
            Revoke access at any time
          </Text>
        </div>
      </div>
    </div>
  );
}
